
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Sparkles, 
  Upload, 
  Heart,
  Zap,
  ArrowRight
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { UserProfile } from '@/types';

interface DashboardHeroProps {
  userProfile: UserProfile;
  streakCount: number;
  hasStreakToday: boolean;
}

const DashboardHero: React.FC<DashboardHeroProps> = ({
  userProfile,
  streakCount,
  hasStreakToday
}) => {
  return (
    <section className="relative overflow-hidden rounded-xl p-6 md:p-8 bg-gradient-to-br from-black/40 to-black/60 border border-neon-purple/20 backdrop-blur-md">
      <div className="absolute inset-0 bg-circuit-pattern opacity-5 pointer-events-none"></div>
      
      <div className="flex flex-col md:flex-row gap-6 md:items-center justify-between">
        <div className="space-y-3">
          <h1 className="text-3xl md:text-4xl font-bold text-gradient-primary flex items-center gap-2">
            <Sparkles className="h-8 w-8 text-neon-purple" />
            Welcome back, {userProfile.name}
          </h1>
          <p className="text-gray-300 md:text-lg max-w-lg">
            Your artist journey is progressing well. Here's an overview of your latest stats and opportunities.
          </p>
          <div className="flex flex-wrap gap-2 md:gap-3 mt-3">
            <Badge variant="outline" className="bg-neon-purple/10 text-neon-purple border-neon-purple/30 py-1">
              Level {userProfile.level}
            </Badge>
            <Badge variant="outline" className="bg-neon-green/10 text-neon-green border-neon-green/30 py-1">
              {streakCount} Day Streak
            </Badge>
            <Badge variant="outline" className="bg-neon-pink/10 text-neon-pink border-neon-pink/30 py-1">
              Emerging Star
            </Badge>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="neon-purple" size="lg" className="gap-2" asChild>
            <Link to="/releases/new">
              <Upload className="h-4 w-4" />
              Upload New Release
            </Link>
          </Button>
          <Button variant="outline" size="lg" className="gap-2 border-neon-blue/30 text-neon-blue hover:bg-neon-blue/10" asChild>
            <Link to="/artist-health">
              <Heart className="h-4 w-4" />
              Check Artist Health
            </Link>
          </Button>
        </div>
      </div>
      
      {/* XP Progress */}
      <div className="mt-6">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-neon-purple" />
            <span className="text-sm font-medium">Artist XP</span>
          </div>
          <span className="text-sm text-gray-400">{userProfile.xp} XP • {500 - ((userProfile.xp || 0) % 500)} to Level {(userProfile.level || 0) + 1}</span>
        </div>
        <Progress 
          value={(userProfile.xp || 0) % 500 / 5} 
          className="h-2 bg-gray-800"
          indicatorClassName="bg-gradient-to-r from-neon-purple to-neon-blue"
        />
      </div>
    </section>
  );
};

export default DashboardHero;
